import clsx from "clsx";
import { omit } from "lodash";
import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import { useDispatch, useSelector } from "src/store";
import { selectProfile, updateProfile } from "src/store/account";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { Profile } from "@dashboard/devices/types";
import { Box, Typography } from "@mui/material";
import { getCountryData } from "@services/countryService";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { ControlledCheckboxField } from "@shared/form/CheckboxField";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { SelectOption } from "@shared/form/SelectField/SelectField.types";
import { TextField } from "@shared/form/TextField";
import { LoadingIcon } from "@shared/Loading";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { SettingsCard } from "../Components/SettingsCard";
import { useStyles as useFormStyles } from "../SettingsPage.style";

import { schema } from "./ProfileEditForm.validation";

type FormValues = Profile;

const typeOfUsageOptions = [
    { value: "nb", label: "Non-business use" },
    { value: "b", label: "Business use" },
];

type CountryDataState = {
    phoneCodes: SelectOption[];
    countryList: SelectOption[];
    mossCountries: string[];
};

type ProfileEditFormProps = {
    profile: API.Profile;
    onSuccess: () => void;
    onCancel: () => void;
};

export const ProfileEditForm = ({ profile, onSuccess, onCancel }: ProfileEditFormProps) => {
    const { classes: formClasses } = useFormStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);

    const currentUserEmail = AuthService.getCurrentUser()?.email;
    const [isLoading, setLoading] = useState(false);
    const profileData = useSelector(selectProfile);
    const profileWithFallback = profile ?? omit(profileData, "password");
    const [data, setData] = useState<CountryDataState>({
        phoneCodes: [],
        countryList: [],
        mossCountries: [],
    });

    const validate = useValidationSchema(schema);

    const onSubmit = async (values: FormValues) => {
        try {
            const response = await dispatch(updateProfile(values)).unwrap();
            const { success, message } = response ?? {};

            if (success) {
                showSuccessToast(message || t("pages:editAccountPage.toast.profileUpdatedSuccess"));
                onSuccess();
            } else if (Array.isArray(message)) {
                const result = message.flatMap((msgObj: { message: string }) => msgObj.message);
                if (result.length > 0) {
                    showErrorToast(result[0]);
                }
            } else {
                showErrorToast(message || t("pages:editAccountPage.toast.profileUpdatedError"));
            }
        } catch (error: unknown) {
            const message =
                (error as { message?: string }).message || t("pages:editAccountPage.toast.profileUpdatedErrorUnknown");
            showErrorToast(message);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const countryData = await getCountryData();
                setData(countryData);
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message);
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, []);

    if (!data.phoneCodes.length || !data.countryList.length) {
        return (
            <SettingsCard title={t("pages:settingsPage.profileData.title")}>
                <Box className={formClasses.loadingContainer}>
                    <LoadingIcon />
                </Box>
            </SettingsCard>
        );
    }

    return (
        <SettingsCard title={t("pages:settingsPage.profileData.title")}>
            <div className={formClasses.formContainer}>
                <Form<FormValues>
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={profileWithFallback}
                    render={({ handleSubmit, submitting, pristine }) => (
                        <form onSubmit={handleSubmit} className={formClasses.formContainer}>
                            <Box className={formClasses.formGrid}>
                                <Box className={formClasses.formField}>
                                    <TextField
                                        name="email"
                                        label={t("pages:editAccountPage.input.email.caption")}
                                        value={currentUserEmail}
                                        disabled
                                        fullWidth
                                    />
                                </Box>
                                <Box className={formClasses.formField}>
                                    <TextField
                                        name="name"
                                        label={t("pages:editAccountPage.input.fullName.caption") + " *"}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box className={clsx(formClasses.formField, formClasses.phoneRow)}>
                                    <ControlledSelectField
                                        name="phoneCode"
                                        label={t("pages:editAccountPage.input.phoneCode.caption")}
                                        options={data.phoneCodes}
                                        fullWidth
                                        disabled={isLoading || submitting}
                                        searchable
                                    />
                                    <TextField
                                        name="phoneNumber"
                                        label={t("pages:editAccountPage.input.phoneNumber.caption") + " *"}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box className={formClasses.formField}>
                                    <TextField
                                        name="address"
                                        label={t("common:label.address")}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box className={formClasses.formField}>
                                    <TextField
                                        name="zipCode"
                                        label={t("pages:editAccountPage.input.zipCode.caption")}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box className={formClasses.formField}>
                                    <TextField
                                        name="city"
                                        label={t("pages:editAccountPage.input.city.caption")}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box className={formClasses.formField}>
                                    <ControlledSelectField
                                        name="country"
                                        label={t("pages:editAccountPage.input.country.caption") + " *"}
                                        options={data.countryList}
                                        fullWidth
                                        disabled={isLoading || submitting}
                                        searchable
                                    />
                                </Box>
                                <Box className={formClasses.formField}>
                                    <ControlledSelectField
                                        name="typeOfUsage"
                                        label={t("pages:editAccountPage.input.typeOfUsage.caption") + " *"}
                                        options={typeOfUsageOptions}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box className={formClasses.formField}>
                                    <TextField
                                        name="companyName"
                                        label={t("pages:editAccountPage.input.company.caption")}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box className={formClasses.formField}>
                                    <TextField
                                        name="fieldOfApplication"
                                        label={t("pages:editAccountPage.input.industry.caption")}
                                        placeholder={t("pages:editAccountPage.input.industry.placeholder")}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                                <Box className={formClasses.fullWidthSection}>
                                    <Typography className={formClasses.sectionHeader}>
                                        {t("pages:editAccountPage.input.emailNotifications.caption")}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" className={formClasses.formInfoText}>
                                        {t("pages:editAccountPage.useInformation")}
                                    </Typography>
                                    <ControlledCheckboxField
                                        name="newsletterChecked"
                                        disableRipple
                                        label={t("pages:editAccountPage.input.newsletter.caption")}
                                        disabled={isLoading}
                                    />
                                </Box>
                            </Box>
                            <div className={formClasses.actionRow}>
                                <SecondaryButton onClick={onCancel}>{t("common:button.cancel")}</SecondaryButton>
                                <PrimaryButton type="submit" disabled={submitting || pristine} loading={submitting}>
                                    {t("common:button.save")}
                                </PrimaryButton>
                            </div>
                        </form>
                    )}
                />
            </div>
        </SettingsCard>
    );
};
